import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, BarChart3, Target, Users, Search, DollarSign, Loader2, MapPin, ChevronRight } from 'lucide-react';
import { useMarketInsights } from '@/hooks/useMarketInsights';
import TrendsSearchDropdown from './TrendsSearchDropdown';

const TrendsLanding = () => {
  const navigate = useNavigate();
  const { data: marketData, isLoading, error } = useMarketInsights();
  const [selectedLocation, setSelectedLocation] = useState<string>('united-states');

  // Fallback data for loading/error states
  const fallbackInsights = [
    {
      title: "Market Analysis",
      description: "Loading latest market trends...",
      value: "...",
      trend: "up" as const
    },
    {
      title: "Employment Data", 
      description: "Analyzing occupation growth patterns...",
      value: "...",
      trend: "up" as const
    },
    {
      title: "Industry Shifts",
      description: "Processing economic indicators...",
      value: "...",
      trend: "stable" as const
    }
  ];

  // Fallback occupations without trend data (only used if API fails)
  const fallbackOccupations = [
    { code: '15-1252', title: 'Software Developers', location: 'california', trend: null },
    { code: '29-1141', title: 'Registered Nurses', location: 'texas', trend: null },
    { code: '25-2031', title: 'Secondary Teachers', location: 'new-york', trend: null },
    { code: '13-2011', title: 'Accountants', location: 'florida', trend: null },
  ];

  // Use real data if available, otherwise fallback
  const marketInsights = marketData?.sector_insights?.map(insight => ({
    title: `${insight.sector} Sector`,
    description: insight.description,
    value: `${insight.growth_rate > 0 ? '+' : ''}${insight.growth_rate}%`,
    trend: insight.trend_direction
  })) || fallbackInsights;

  // Only use fallback occupations if API explicitly failed (not just loading)
  const featuredOccupations = error && !marketData?.trending_occupations
    ? fallbackOccupations
    : marketData?.trending_occupations?.map(occ => ({
        code: occ.occupation_code,
        title: occ.title,
        location: occ.location,
        trend: occ.growth_5yr ? `+${occ.growth_5yr}%` : null
      })) || [];

  // Popular occupations for trends analysis
  const popularOccupations = [
    { code: '15-1252', slug: '15-1252-software-developers', title: 'Software Developers', description: 'Design and develop software applications and systems', category: 'Technology' },
    { code: '29-1141', slug: '29-1141-registered-nurses', title: 'Registered Nurses', description: 'Provide and coordinate patient care and educate patients', category: 'Healthcare' },
    { code: '15-2051', slug: '15-2051-data-scientists', title: 'Data Scientists', description: 'Analyze and interpret complex data to help organizations make decisions', category: 'Technology' },
    { code: '11-2021', slug: '11-2021-marketing-managers', title: 'Marketing Managers', description: 'Plan, direct, or coordinate marketing policies and programs', category: 'Business' },
    { code: '13-2051', slug: '13-2051-financial-analysts', title: 'Financial Analysts', description: 'Conduct quantitative analyses of information affecting investment programs', category: 'Finance' },
    { code: '25-2021', slug: '25-2021-elementary-school-teachers', title: 'Elementary School Teachers', description: 'Teach academic and social skills to students in elementary grades', category: 'Education' },
    { code: '11-3021', slug: '11-3021-computer-and-information-systems-managers', title: 'Computer and Information Systems Managers', description: 'Plan, coordinate, and direct computer-related activities', category: 'Technology' },
    { code: '13-2011', slug: '13-2011-accountants-and-auditors', title: 'Accountants and Auditors', description: 'Examine, analyze, and interpret accounting records', category: 'Finance' }
  ];

  const locations = [
    { value: 'united-states', label: 'United States' },
    { value: 'california', label: 'California' },
    { value: 'texas', label: 'Texas' },
    { value: 'new-york', label: 'New York' },
    { value: 'florida', label: 'Florida' },
    { value: 'illinois', label: 'Illinois' },
    { value: 'pennsylvania', label: 'Pennsylvania' },
    { value: 'ohio', label: 'Ohio' },
    { value: 'georgia', label: 'Georgia' },
    { value: 'north-carolina', label: 'North Carolina' },
    { value: 'michigan', label: 'Michigan' },
    { value: 'new-jersey', label: 'New Jersey' },
    { value: 'virginia', label: 'Virginia' },
    { value: 'washington', label: 'Washington' },
    { value: 'arizona', label: 'Arizona' },
  ];

  const filteredOccupations = popularOccupations; // Show all occupations since search is now handled by TrendsSearchDropdown

  const handleOccupationSelect = (occupationSlug: string) => {
    navigate(`/trends/${occupationSlug}/${selectedLocation}`);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Market Insights */}
      <div className="grid md:grid-cols-3 gap-6">
        {isLoading ? (
          // Loading state
          Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="shadow-sm">
              <CardContent>
                <div className="flex items-center justify-between mb-2">
                  <div className="h-4 bg-muted rounded animate-pulse w-24"></div>
                  <div className="h-8 bg-muted rounded animate-pulse w-16"></div>
                </div>
                <div className="h-4 bg-muted rounded animate-pulse w-full"></div>
              </CardContent>
            </Card>
          ))
        ) : error ? (
          // Error state with fallback
          <Card className="md:col-span-3 shadow-sm border-destructive/20">
            <CardContent className="text-center">
              <p className="text-muted-foreground mb-2">Unable to load latest market data</p>
              <p className="text-sm text-muted-foreground">Showing cached insights</p>
            </CardContent>
          </Card>
        ) : null}
        
        {marketInsights.slice(0, 3).map((insight, index) => (
          <Card key={index} className="shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105">
            <CardContent>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">{insight.title}</h3>
                <div className={`text-2xl font-bold ${
                  insight.trend === 'up' ? 'text-success' : 
                  insight.trend === 'down' ? 'text-destructive' : 'text-blue-600'
                }`}>
                  {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : insight.value}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{insight.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Occupation & Location Selector */}
      <Card className="shadow-lg bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Start Your Employment Trends Analysis
          </CardTitle>
          <CardDescription>
            Select an occupation and location to explore comprehensive employment data and salary trends
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Location Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Location</label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="bg-background border-2 focus:border-primary">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-background border-2 shadow-lg z-50">
                {locations.map((location) => (
                  <SelectItem key={location.value} value={location.value}>
                    {location.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Occupation Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Search Occupations</label>
            <TrendsSearchDropdown
              placeholder="Search by occupation title..."
              selectedLocation={selectedLocation}
              className="w-full"
            />
          </div>

          {/* Popular Occupations Grid */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Popular Occupations</h4>
              <Badge variant="outline" className="text-xs">
                {filteredOccupations.length} occupations
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
              {filteredOccupations.map((occupation) => (
                <Card 
                  key={`popular-${occupation.code}`}
                  className="p-4 cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-102 hover:border-primary/50"
                  onClick={() => handleOccupationSelect(occupation.slug)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-sm">{occupation.title}</h5>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {occupation.description}
                      </p>
                      <Badge variant="secondary" className="text-xs mt-2">
                        {occupation.category}
                      </Badge>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-2" />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Featured Trending Occupations */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Trending Occupations
            {marketData?.market_metadata && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {marketData.market_metadata.coverage_period}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {isLoading ? 'Loading real-time career growth data...' : 
             error ? 'Popular career paths with strong growth momentum' :
             `Live data from ${marketData?.market_metadata?.total_occupations_analyzed || 0}+ analyzed occupations`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="grid md:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="h-5 bg-muted rounded animate-pulse w-32 mb-2"></div>
                    <div className="h-4 bg-muted rounded animate-pulse w-24"></div>
                  </div>
                  <div className="text-right">
                    <div className="h-6 bg-muted rounded animate-pulse w-12 mb-1"></div>
                    <div className="h-3 bg-muted rounded animate-pulse w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredOccupations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No trending occupation data available at this time.</p>
              <p className="text-sm mt-2">Please try again later or search for specific occupations above.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {featuredOccupations.map((occ, index) => (
                <div 
                  key={`featured-${occ.code || occ.title}-${index}`}
                  className="group flex items-center justify-between p-4 border rounded-lg hover:bg-accent hover:text-accent-foreground cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-md"
                  onClick={() => navigate(`/trends/${occ.code}-${occ.title.toLowerCase().replace(/\s+/g, '-')}/${occ.location}`)}
                >
                  <div>
                    <h4 className="font-medium">{occ.title}</h4>
                    <p className="text-sm text-muted-foreground capitalize group-hover:text-accent-foreground/80">{occ.location}</p>
                  </div>
                  <div className="text-right">
                    {occ.trend ? (
                      <>
                        <div className="text-success font-semibold group-hover:text-accent-foreground">{occ.trend}</div>
                        <div className="text-xs text-muted-foreground group-hover:text-accent-foreground/80">5-year growth</div>
                      </>
                    ) : (
                      <>
                        <div className="text-muted-foreground font-semibold group-hover:text-accent-foreground">—</div>
                        <div className="text-xs text-muted-foreground group-hover:text-accent-foreground/80">Data unavailable</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Use Case Scenarios */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" onClick={() => navigate('/?focus=search')}>
          <CardContent className="text-center">
            <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">Planning a Career Change?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Discover emerging opportunities and growth trends in your field
            </p>
            <Button variant="outline" size="sm" className="w-full">
              Explore Careers
            </Button>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" onClick={() => navigate('/?focus=search')}>
          <CardContent className="text-center">
            <div className="bg-success/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
              <DollarSign className="h-6 w-6 text-success" />
            </div>
            <h3 className="font-semibold mb-2">Negotiating Salary?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Get data-backed insights on competitive compensation ranges
            </p>
            <Button variant="outline" size="sm" className="w-full">
              Check Salaries
            </Button>
          </CardContent>
        </Card>

        <Card className="shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" onClick={() => navigate('/?focus=search')}>
          <CardContent className="text-center">
            <div className="bg-blue-500/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
              <Search className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold mb-2">Exploring New Markets?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Compare regional opportunities and market dynamics
            </p>
            <Button variant="outline" size="sm" className="w-full">
              Compare Markets
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Social Proof & Stats */}
      <Card className="shadow-lg bg-gradient-to-br from-accent/5 to-transparent">
        <CardContent className="p-4 sm:p-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">
                {marketData?.market_metadata?.total_occupations_analyzed || '850+'}
              </div>
              <p className="text-sm text-muted-foreground">Occupations Analyzed</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-success mb-2">28</div>
              <p className="text-sm text-muted-foreground">Years of Historical Data</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">50+</div>
              <p className="text-sm text-muted-foreground">States & Metro Areas</p>
            </div>
          </div>
          <div className="text-center mt-6">
            <p className="text-muted-foreground text-sm">
              Trusted by career professionals, recruiters, and job seekers nationwide
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="text-center shadow-lg bg-gradient-to-br from-accent/10 to-transparent">
        <CardContent className="p-4 sm:p-8">
          <h3 className="text-xl font-semibold mb-2">Ready to Analyze Employment Trends?</h3>
          <p className="text-muted-foreground mb-4">
            Use the occupation selector above to start exploring comprehensive employment data and salary trends - all free, no account required
          </p>
          <div className="flex justify-center gap-3">
            <Button 
              size="lg" 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="gap-2 hover:scale-105 transition-transform duration-200"
            >
              <Users className="h-4 w-4" />
              Start Analysis
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrendsLanding;